import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Calendar } from "lucide-react";
import api from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { Chip, Pagination } from "./Recipes";

export default function Blog() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  useSeo({ title: "Blog | Healthy Roots Holistics", description: "Natural health education, articles and wellness insights from Healthy Roots." });

  useEffect(() => {
    const p = new URLSearchParams({ page, per_page: 9 });
    if (search) p.set("search", search);
    if (category) p.set("category", category);
    api.get(`/public/blog?${p}`).then((r) => setData(r.data)).catch(() => {});
  }, [search, category, page]);

  const cats = ["Natural Health", "Nutrition", "Lifestyle", "Education"];

  return (
    <div className="pt-28 pb-24" data-testid="blog-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-sm uppercase tracking-widest text-emerald-700 font-semibold">Insights & Education</span>
          <h1 className="text-4xl sm:text-5xl mt-2 text-emerald-950">The Blog</h1>
        </div>
        <div className="flex flex-wrap gap-3 items-center justify-between mb-10">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search articles…" data-testid="blog-search"
              className="w-full rounded-full border border-stone-300 pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-emerald-600 outline-none bg-white" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Chip active={!category} onClick={() => { setCategory(""); setPage(1); }}>All</Chip>
            {cats.map((c) => <Chip key={c} active={category === c} onClick={() => { setCategory(c); setPage(1); }}>{c}</Chip>)}
          </div>
        </div>
        {data.items.length === 0 ? <p className="text-center text-stone-500 py-20">No articles yet.</p> : (
          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                className="group bg-white rounded-3xl border border-stone-200/80 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all overflow-hidden">
                <div className="h-48 overflow-hidden"><img src={p.featured_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-6">
                  <div className="flex gap-2 mb-3">{(p.categories || []).slice(0, 2).map((c) => <span key={c} className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">{c}</span>)}</div>
                  <h3 className="text-xl text-emerald-900 mb-2">{p.title}</h3>
                  <p className="text-sm text-stone-600 line-clamp-2">{p.excerpt}</p>
                  <div className="flex items-center gap-2 mt-4 text-xs text-stone-500 font-mono"><Calendar size={13} /> {(p.published_at || "").slice(0, 10)} · {p.author}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
        <Pagination data={data} setPage={setPage} />
      </div>
    </div>
  );
}
